// src/pages/WikiControl.tsx
import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
  useRef,
} from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useDataProvider, useNotification } from "@refinedev/core";
import {
  Button,
  Card,
  Drawer,
  Form,
  Input,
  Modal,
  Result,
  Select,
  Space,
  Table,
  Typography,
  theme as antdTheme,
  Popconfirm,
  Segmented,
  Divider,
  Alert,
  Tooltip,
} from "antd";
import {
  EditOutlined,
  SaveOutlined,
  CloseOutlined,
  HistoryOutlined,
  DeleteOutlined,
  PlusOutlined,
  SettingOutlined,
  UndoOutlined,
  DiffOutlined,
  EyeOutlined,
  ReloadOutlined,
} from "@ant-design/icons";

import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import * as Diff from "diff";

import {
  MDXEditor,
  headingsPlugin,
  listsPlugin,
  quotePlugin,
  thematicBreakPlugin,
  linkPlugin,
  linkDialogPlugin,
  markdownShortcutPlugin,
  toolbarPlugin,
  UndoRedo,
  BoldItalicUnderlineToggles,
  ListsToggle,
  CreateLink,
  InsertThematicBreak,
} from "@mdxeditor/editor";

import { usePGBroadcastRefresh } from "../realtime/usePGBroadcastRefresh";

const WIKI_SCHEMA = "wiki";
const PAGES_RESOURCE = "pages";
const REVS_RESOURCE = "page_revisions";

const { Title, Text } = Typography;
const { TextArea } = Input;

// --- Types ---
type WikiPage = {
  id: string;
  path: string;
  title: string;
  content_md: string;
  updated_at: string;
};

type WikiRevision = {
  id: string;
  page_id: string;
  revision_num: number;
  title: string;
  content_md: string;
  created_at: string;
};

type EditorMode = "rich" | "raw" | "raw-preview" | "preview";

// --- Sub-Components ---

const TopFocusAnchor: React.FC = () => {
  const divRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (divRef.current) {
      divRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
      divRef.current.focus();
    }
  }, []);
  return <div ref={divRef} tabIndex={-1} style={{ outline: "none" }} />;
};

const DiffViewer: React.FC<{ oldText: string; newText: string }> = ({
  oldText,
  newText,
}) => {
  const { token } = antdTheme.useToken();
  const diffGroups = useMemo(
    () => Diff.diffLines(oldText || "", newText || ""),
    [oldText, newText],
  );

  return (
    <div
      style={{
        fontFamily: "monospace",
        whiteSpace: "pre-wrap",
        fontSize: "13px",
        lineHeight: 1.5,
        border: `1px solid ${token.colorBorder}`,
        borderRadius: token.borderRadius,
        overflow: "hidden",
      }}
    >
      {diffGroups.map((part, index) => {
        let bg = "transparent";
        let color = token.colorText;

        if (part.added) {
          bg = token.colorSuccessBg;
          color = token.colorSuccessText;
        } else if (part.removed) {
          bg = token.colorErrorBg;
          color = token.colorErrorText;
        }

        return (
          <div
            key={index}
            style={{
              backgroundColor: bg,
              color: color,
              display: "block",
              padding: "0 4px",
            }}
          >
            {part.value}
          </div>
        );
      })}
    </div>
  );
};

const WikiEditor: React.FC<{
  mode: EditorMode;
  setMode: (m: EditorMode) => void;
  value: string;
  onChange: (val: string) => void;
  minHeight?: number;
}> = ({ mode, setMode, value, onChange, minHeight = 400 }) => {
  const { token } = antdTheme.useToken();
  const isDark = document.body.classList.contains("app-dark");

  const showRaw = mode === "raw" || mode === "raw-preview";
  const showRich = mode === "rich";
  const showPreview = mode === "preview" || mode === "raw-preview";

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
      <div style={{ display: "flex", justifyContent: "flex-end" }}>
        <Select
          value={mode}
          onChange={setMode}
          size="small"
          style={{ width: 160 }}
          options={[
            { label: "Rich Editor", value: "rich" },
            { label: "Raw Markdown", value: "raw" },
            { label: "Raw + Preview", value: "raw-preview" },
            { label: "Preview Only", value: "preview" },
          ]}
        />
      </div>

      <div
        style={{
          border: `1px solid ${token.colorBorder}`,
          borderRadius: token.borderRadius,
          backgroundColor: showRaw ? token.colorBgContainer : "transparent",
          overflow: "hidden",
          display: "flex",
          flexDirection: mode === "raw-preview" ? "row" : "column",
        }}
      >
        {showRaw && (
          <TextArea
            value={value}
            onChange={(e) => onChange(e.target.value)}
            placeholder="Markdown supported..."
            style={{
              flex: 1,
              fontFamily: "monospace",
              border: "none",
              borderRadius: 0,
              boxShadow: "none",
              resize: "vertical",
              minHeight: minHeight,
              backgroundColor: "transparent",
              color: token.colorText,
              borderRight: showPreview
                ? `1px solid ${token.colorBorderSecondary}`
                : "none",
            }}
          />
        )}

        {showRich && (
          <div
            className={isDark ? "dark-theme" : ""}
            style={
              {
                minHeight: minHeight,
                backgroundColor: token.colorBgContainer,
                width: "100%",
                "--mdx-base-color": token.colorText,
                "--mdx-text-color": token.colorText,
                "--mdx-bg-color": token.colorBgContainer,
                "--mdx-border-color": token.colorBorder,
                "--mdx-toolbar-bg-color": token.colorFillQuaternary,
                "--mdx-toolbar-text-color": token.colorTextDescription,
                "--mdx-toolbar-active-bg-color": token.colorFillSecondary,
                "--mdx-toolbar-hover-bg-color": token.colorFillTertiary,
                "--mdx-dropdown-bg-color": token.colorBgElevated,
                "--mdx-dropdown-text-color": token.colorText,
              } as React.CSSProperties
            }
          >
            <MDXEditor
              markdown={value}
              onChange={(v) => onChange(v)}
              contentEditableClassName="prose"
              plugins={[
                headingsPlugin(),
                listsPlugin(),
                quotePlugin(),
                thematicBreakPlugin(),
                linkPlugin(),
                linkDialogPlugin(),
                markdownShortcutPlugin(),
                toolbarPlugin({
                  toolbarContents: () => (
                    <>
                      <UndoRedo />
                      <BoldItalicUnderlineToggles />
                      <ListsToggle />
                      <CreateLink />
                      <InsertThematicBreak />
                    </>
                  ),
                }),
              ]}
            />
          </div>
        )}

        {showPreview && (
          <div
            style={{
              flex: 1,
              padding: "12px",
              minHeight: minHeight,
              maxHeight: 600,
              overflowY: "auto",
              backgroundColor: token.colorFillQuaternary,
              color: token.colorText,
            }}
          >
            {mode === "raw-preview" && (
              <div
                style={{
                  fontSize: 11,
                  fontWeight: 600,
                  marginBottom: 8,
                  textTransform: "uppercase",
                  color: token.colorTextDescription,
                  opacity: 0.8,
                }}
              >
                Live Preview
              </div>
            )}
            {value ? (
              <ReactMarkdown
                remarkPlugins={[remarkGfm]}
                components={{
                  p: ({ node, ...props }) => (
                    <p style={{ color: token.colorText }} {...props} />
                  ),
                  li: ({ node, ...props }) => (
                    <li style={{ color: token.colorText }} {...props} />
                  ),
                  h1: ({ node, ...props }) => (
                    <h1 style={{ color: token.colorTextHeading }} {...props} />
                  ),
                  h2: ({ node, ...props }) => (
                    <h2 style={{ color: token.colorTextHeading }} {...props} />
                  ),
                  h3: ({ node, ...props }) => (
                    <h3 style={{ color: token.colorTextHeading }} {...props} />
                  ),
                  a: ({ node, ...props }) => (
                    <a style={{ color: token.colorLink }} {...props} />
                  ),
                }}
              >
                {value}
              </ReactMarkdown>
            ) : (
              <span
                style={{
                  color: token.colorTextPlaceholder,
                  fontStyle: "italic",
                }}
              >
                Nothing to preview
              </span>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

const HistoryDrawer: React.FC<{
  open: boolean;
  onClose: () => void;
  pageId?: string;
  wikiPath: string;
  currentContent: string;
  onRestore: (rev: WikiRevision) => Promise<void>;
  dp: any;
}> = ({ open, onClose, pageId, wikiPath, currentContent, onRestore, dp }) => {
  const [revisions, setRevisions] = useState<WikiRevision[]>([]);
  const [loading, setLoading] = useState(false);
  const [viewRev, setViewRev] = useState<WikiRevision | null>(null);
  const [viewMode, setViewMode] = useState<"preview" | "diff">("preview");

  useEffect(() => {
    if (open && pageId) {
      setLoading(true);
      dp.getList({
        resource: REVS_RESOURCE,
        pagination: { current: 1, pageSize: 100 },
        filters: [{ field: "page_id", operator: "eq", value: pageId }],
        sorters: [{ field: "revision_num", order: "desc" }],
        meta: { profile: WIKI_SCHEMA },
      })
        .then((res: any) => setRevisions((res.data as WikiRevision[]) || []))
        .catch(() => setRevisions([]))
        .finally(() => setLoading(false));
    }
  }, [open, pageId, dp]);

  return (
    <Drawer
      open={open}
      onClose={onClose}
      placement="left"
      title={`History: ${wikiPath}`}
      width={window.innerWidth < 768 ? "100%" : 700}
      destroyOnClose
    >
      <TopFocusAnchor />
      <Table
        dataSource={revisions}
        rowKey="id"
        size="small"
        loading={loading}
        pagination={{ pageSize: 10 }}
      >
        <Table.Column title="Rev" dataIndex="revision_num" width={60} />
        <Table.Column
          title="Date"
          dataIndex="created_at"
          width={160}
          render={(v) => (
            <Text style={{ fontSize: 13 }}>
              {String(v).replace("T", " ").slice(0, 16)}
            </Text>
          )}
        />
        <Table.Column title="Title" dataIndex="title" ellipsis />
        <Table.Column
          title="Actions"
          width={180}
          render={(_, r: WikiRevision) => (
            <Space>
              <Button
                size="small"
                onClick={() => {
                  setViewRev(r);
                  setViewMode("preview");
                }}
              >
                View
              </Button>
              <Popconfirm
                title="Restore this version?"
                description="This will overwrite the current page content."
                onConfirm={() => onRestore(r)}
              >
                <Button size="small" icon={<UndoOutlined />}>
                  Restore
                </Button>
              </Popconfirm>
            </Space>
          )}
        />
      </Table>

      <Modal
        open={!!viewRev}
        onCancel={() => setViewRev(null)}
        footer={null}
        width={900}
        title={
          <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
            <span>Revision {viewRev?.revision_num}</span>
            <Segmented
              value={viewMode}
              onChange={(v) => setViewMode(v as "preview" | "diff")}
              options={[
                { label: "Preview", value: "preview", icon: <EyeOutlined /> },
                {
                  label: "Diff vs Current",
                  value: "diff",
                  icon: <DiffOutlined />,
                },
              ]}
            />
          </div>
        }
      >
        {viewRev && (
          <div>
            <div style={{ marginBottom: 12 }}>
              <Text type="secondary" style={{ fontSize: 12 }}>
                Title Snapshot:{" "}
              </Text>
              <Text strong>{viewRev.title}</Text>
            </div>

            {viewMode === "preview" ? (
              <div
                style={{
                  padding: 12,
                  background: "rgba(0,0,0,0.02)",
                  border: "1px solid #ddd",
                  borderRadius: 8,
                  maxHeight: "60vh",
                  overflowY: "auto",
                }}
              >
                <ReactMarkdown remarkPlugins={[remarkGfm]}>
                  {viewRev.content_md}
                </ReactMarkdown>
              </div>
            ) : (
              <div style={{ maxHeight: "60vh", overflowY: "auto" }}>
                <DiffViewer
                  oldText={currentContent}
                  newText={viewRev.content_md}
                />
              </div>
            )}
          </div>
        )}
      </Modal>
    </Drawer>
  );
};

// --- Main Component ---
const WikiControl: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { open: notify } = useNotification();
  const { token } = antdTheme.useToken();

  const getDp = useDataProvider();
  const dp = getDp();

  // State
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState<WikiPage | null>(null);
  const [notFound, setNotFound] = useState(false);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);

  // ✅ Auto-refresh coordination
  const [refreshKey, setRefreshKey] = useState(0);
  const [remoteDirty, setRemoteDirty] = useState(false);

  // Edit State
  const [title, setTitle] = useState("");
  const [content, setContent] = useState("");
  const [pageMinWidth, setPageMinWidth] = useState<string | number>(800);

  const wikiPath = useMemo(() => {
    const p = location.pathname.startsWith("/wiki")
      ? location.pathname.slice("/wiki".length)
      : location.pathname;
    return p.replace(/^\/+/, "").trim();
  }, [location.pathname]);

  const [mode, setMode] = useState<EditorMode>(() => {
    try {
      return (
        (localStorage.getItem(`wiki:mode:${wikiPath}`) as EditorMode) || "raw"
      );
    } catch {
      return "raw";
    }
  });

  // ✅ Realtime subscribe to wiki changes
  // Note: This subscribes to ANY page update in wiki.pages/wiki.page_revisions.
  // We then only "act" (reload / banner) if it impacts the currently viewed path.
  const realtimeTopics = useMemo(() => {
    return ["wiki.pages", "wiki.page_revisions"];
  }, []);

  usePGBroadcastRefresh({
    topics: realtimeTopics,
    debounceMs: 200,
    enabled: true,
    onChange: () => {
      // If user is editing, DO NOT clobber their content.
      // Instead show a banner letting them choose to reload.
      if (editing) {
        setRemoteDirty(true);
      } else {
        setRefreshKey((k) => k + 1);
      }
    },
  });

  const loadPage = useCallback(async () => {
    if (!wikiPath) return;
    setLoading(true);
    try {
      const res: any = await dp.getList({
        resource: PAGES_RESOURCE,
        pagination: { current: 1, pageSize: 1 },
        filters: [{ field: "path", operator: "eq", value: wikiPath }],
        sorters: [{ field: "updated_at", order: "desc" }],
        meta: { profile: WIKI_SCHEMA },
      });

      const row = (res?.data?.[0] as WikiPage) || null;

      setPage(row);
      setNotFound(!row);

      // Only reset the editor fields if we're not editing.
      // If we ARE editing, this function is only called from explicit actions (reload banner / after save/restore/delete).
      if (!editing) {
        setTitle(row?.title || wikiPath.split("/").pop() || "Untitled");
        setContent(row?.content_md || "");
        setEditing(false);
      }
    } catch (e: any) {
      notify?.({ type: "error", message: "Failed to load page" });
      setNotFound(true);
    } finally {
      setLoading(false);
    }
  }, [wikiPath, dp, notify, editing]);

  useEffect(() => {
    loadPage();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadPage, refreshKey]);

  const handleSave = async () => {
    if (!title.trim())
      return notify?.({ type: "warning", message: "Title required" });
    setSaving(true);
    try {
      if (page?.id) {
        await dp.update({
          resource: PAGES_RESOURCE,
          id: page.id,
          variables: { title, content_md: content },
          meta: { profile: WIKI_SCHEMA },
        });
      } else {
        await dp.create({
          resource: PAGES_RESOURCE,
          variables: { path: wikiPath, title, content_md: content },
          meta: { profile: WIKI_SCHEMA },
        });
      }
      notify?.({ type: "success", message: "Saved" });
      setEditing(false);
      setRemoteDirty(false);
      await loadPage();
    } catch (e: any) {
      notify?.({
        type: "error",
        message: "Save failed",
        description: e?.message,
      });
    } finally {
      setSaving(false);
    }
  };

  const handleRestore = async (rev: WikiRevision) => {
    if (!page?.id) return;
    try {
      await dp.update({
        resource: PAGES_RESOURCE,
        id: page.id,
        variables: { title: rev.title, content_md: rev.content_md },
        meta: { profile: WIKI_SCHEMA },
      });
      notify?.({ type: "success", message: "Restored successfully" });
      setHistoryOpen(false);
      setEditing(false);
      setRemoteDirty(false);
      await loadPage();
    } catch (e: any) {
      notify?.({
        type: "error",
        message: "Restore failed",
        description: e?.message,
      });
    }
  };

  const handleDelete = () => {
    if (!page?.id) return;
    dp.deleteOne({
      resource: PAGES_RESOURCE,
      id: page.id,
      meta: { profile: WIKI_SCHEMA },
    })
      .then(() => {
        notify?.({ type: "success", message: "Deleted" });
        setEditing(false);
        setRemoteDirty(false);
        loadPage();
      })
      .catch((e: any) =>
        notify?.({
          type: "error",
          message: "Delete failed",
          description: e?.message,
        }),
      );
  };

  const persistMode = (m: EditorMode) => {
    setMode(m);
    try {
      localStorage.setItem(`wiki:mode:${wikiPath}`, m);
    } catch {
      // ignore
    }
  };

  const crumbs = useMemo(() => {
    const parts = wikiPath.split("/").filter(Boolean);
    let acc = "";
    return parts.map((p) => {
      acc = acc ? `${acc}/${p}` : p;
      return { label: p, href: `/wiki/${acc}` };
    });
  }, [wikiPath]);

  if (!wikiPath)
    return (
      <Card>
        <Title level={3}>Wiki Root</Title>
      </Card>
    );
  if (loading) return <Card loading />;

  const layoutStyle: React.CSSProperties = {
    width: typeof pageMinWidth === "number" ? `${pageMinWidth}px` : "100%",
    margin: "0 auto",
    display: "flex",
    flexDirection: "column",
    gap: 12,
  };

  return (
    <div style={{ width: "100%", overflowX: "auto" }}>
      <div style={layoutStyle}>
        {/* ✅ Remote update banner (only while editing) */}
        {editing && remoteDirty && (
          <Alert
            type="warning"
            showIcon
            message="This page changed on the server while you were editing."
            description={
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                <Button
                  size="small"
                  icon={<ReloadOutlined />}
                  onClick={async () => {
                    // Reload server state and overwrite local edits
                    setEditing(false);
                    setRemoteDirty(false);
                    await loadPage();
                  }}
                >
                  Reload server version
                </Button>
                <Button
                  size="small"
                  onClick={() => setRemoteDirty(false)}
                  icon={<CloseOutlined />}
                >
                  Dismiss
                </Button>
              </div>
            }
          />
        )}

        {/* Top Toolbar */}
        <Card bodyStyle={{ padding: "8px 16px" }}>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              flexWrap: "wrap",
              gap: 12,
            }}
          >
            <Space
              wrap
              split={<Text type="secondary">/</Text>}
              style={{ fontSize: 14 }}
            >
              <a
                onClick={(e) => {
                  e.preventDefault();
                  navigate("/wiki");
                }}
                style={{ color: token.colorTextSecondary }}
              >
                wiki
              </a>
              {crumbs.map((c) => (
                <a
                  key={c.href}
                  onClick={(e) => {
                    e.preventDefault();
                    navigate(c.href);
                  }}
                  style={{ fontWeight: 500 }}
                >
                  {c.label}
                </a>
              ))}
            </Space>

            <Space wrap>
              <Select
                value={pageMinWidth}
                onChange={setPageMinWidth}
                size="small"
                style={{ width: 140 }}
                options={[
                  { label: "Full Width", value: "100%" },
                  { label: "Small (520px)", value: 520 },
                  { label: "Medium (800px)", value: 800 },
                ]}
                suffixIcon={<SettingOutlined />}
              />

              {/* ✅ Manual refresh (also useful to confirm realtime) */}
              <Tooltip title="Refresh">
                <Button
                  size="small"
                  icon={<ReloadOutlined />}
                  onClick={() => {
                    if (editing) setRemoteDirty(true);
                    else setRefreshKey((k) => k + 1);
                  }}
                />
              </Tooltip>

              <Button
                size="small"
                icon={<HistoryOutlined />}
                onClick={() => setHistoryOpen(true)}
                disabled={!page?.id}
              >
                History
              </Button>

              {!editing ? (
                <>
                  {notFound ? (
                    <Button
                      size="small"
                      type="primary"
                      icon={<PlusOutlined />}
                      onClick={() => {
                        setEditing(true);
                        setRemoteDirty(false);
                      }}
                    >
                      Create
                    </Button>
                  ) : (
                    <Button
                      size="small"
                      type="primary"
                      icon={<EditOutlined />}
                      onClick={() => {
                        setEditing(true);
                        setRemoteDirty(false);
                      }}
                    >
                      Edit
                    </Button>
                  )}
                  <Popconfirm
                    title="Delete page?"
                    onConfirm={handleDelete}
                    disabled={!page?.id}
                  >
                    <Button
                      size="small"
                      danger
                      icon={<DeleteOutlined />}
                      disabled={!page?.id}
                    />
                  </Popconfirm>
                </>
              ) : (
                <>
                  <Button
                    size="small"
                    type="primary"
                    icon={<SaveOutlined />}
                    onClick={handleSave}
                    loading={saving}
                  >
                    Save
                  </Button>
                  <Button
                    size="small"
                    icon={<CloseOutlined />}
                    onClick={() => {
                      setEditing(false);
                      setRemoteDirty(false);
                      setTitle(page?.title || "");
                      setContent(page?.content_md || "");
                    }}
                  >
                    Cancel
                  </Button>
                </>
              )}
            </Space>
          </div>
        </Card>

        {/* Main Content Area */}
        {notFound && !editing ? (
          <Result
            status="404"
            title="Page not found"
            extra={
              <Button
                type="primary"
                onClick={() => {
                  setEditing(true);
                  setRemoteDirty(false);
                }}
              >
                Create Page
              </Button>
            }
          />
        ) : (
          <Card>
            {!editing ? (
              <div style={{ minHeight: 200 }}>
                <Title level={2} style={{ marginTop: 0 }}>
                  {page?.title}
                </Title>
                {page && (
                  <Text
                    type="secondary"
                    style={{ display: "block", marginBottom: 16, fontSize: 12 }}
                  >
                    Last updated:{" "}
                    {String(page.updated_at).replace("T", " ").slice(0, 16)}
                  </Text>
                )}
                <Divider style={{ margin: "12px 0 24px 0" }} />

                <ReactMarkdown
                  remarkPlugins={[remarkGfm]}
                  components={{
                    h1: ({ node, ...p }) => (
                      <h1 style={{ color: token.colorTextHeading }} {...p} />
                    ),
                    a: ({ node, ...p }) => (
                      <a style={{ color: token.colorLink }} {...p} />
                    ),
                  }}
                >
                  {page?.content_md || "_No content_"}
                </ReactMarkdown>
              </div>
            ) : (
              <Form layout="vertical">
                <Form.Item label="Page Title">
                  <Input
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    size="large"
                  />
                </Form.Item>
                <Form.Item label="Content">
                  <WikiEditor
                    mode={mode}
                    setMode={persistMode}
                    value={content}
                    onChange={setContent}
                  />
                </Form.Item>
              </Form>
            )}
          </Card>
        )}
      </div>

      <HistoryDrawer
        open={historyOpen}
        onClose={() => setHistoryOpen(false)}
        pageId={page?.id}
        wikiPath={wikiPath}
        currentContent={page?.content_md || ""}
        onRestore={handleRestore}
        dp={dp}
      />
    </div>
  );
};

export default WikiControl;

